import { createContext, PropsWithChildren, useEffect, useState } from "react";
import {
  GetLessonsByLessonIdUnitsApiResponse,
  IUnitCompleted,
  LessonResponse,
  umbracoApi,
  useGetLessonsByIdQuery,
  useGetLessonsByLessonIdUnitsQuery,
  usePostActivitiesByFeatureIdMutation,
  usePostLessonsByLessonIdUnitsAndIdMutation,
} from "../store/api/umbraco-api";
import { UnitContext } from "./unit-context";
import { sessionTracker } from "../session-tracker/session-tracker";

interface LessonContextValue extends UnitContext {
  isLoading: boolean;
  isError: boolean;
  lesson: LessonResponse | undefined;
  units: GetLessonsByLessonIdUnitsApiResponse;
  currentUnitId: number | null;
  setCurrentUnitId: (id: number | null) => void;
}

export const LessonContext = createContext<LessonContextValue | null>(null);

interface LessonProviderProps {
  lessonId: number;
}

export const LessonProvider = ({
  lessonId,
  children,
}: PropsWithChildren<LessonProviderProps>) => {
  // Fetch lesson and units data
  const lessonState = useGetLessonsByIdQuery({ id: lessonId });
  const unitsState = useGetLessonsByLessonIdUnitsQuery({ lessonId });

  // Session State Debug for Lesson
  useEffect(() => {
    if (lessonState.data && !lessonState.isLoading) {
      console.group("🔄 Lesson Session State");
      console.log("📋 Lesson Info:", {
        lessonId: lessonId,
        lessonName: lessonState.data?.name,
        lessonStatus: lessonState.data?.status,
        totalUnits: lessonState.data?.totalUnits,
        completedUnits: lessonState.data?.completedUnits,
        finishMessage: lessonState.data?.finishMessage
      });
      
      if (lessonState.data?.status === 'Completed') {
        console.warn("⚠️ Lesson is Completed - new completions may be rejected");
      }
      
      console.groupEnd();
    }
  }, [lessonState.data, lessonState.isLoading, lessonId]);

  const [fetchUnitContent, unitContentState] =
    umbracoApi.useLazyGetLessonsByLessonIdUnitsAndUnitIdQuery();
  const [postUnitCompletion, unitCompletionState] =
    usePostLessonsByLessonIdUnitsAndIdMutation();

  // unit activity post request
  const [postActivity, activityState] = usePostActivitiesByFeatureIdMutation();

  const [currentUnitId, setCurrentUnitId] = useState<number | null>(
    parseInt(localStorage.getItem(`lesson:${lessonId}:currentUnitId`) || "0") || null
  );

  // fetch unit content when unitId is changed
  useEffect(() => {
    if (currentUnitId) {
      // ✅ Enhanced Learning Minutes: Start session tracking for new unit
      sessionTracker.startSession(currentUnitId, lessonId);

      // Original activity tracking logic (for navigation between units)
      if (!activityState.isLoading && unitContentState.fulfilledTimeStamp) {
        const activityData = {
          unitId: currentUnitId,
          startTime: new Date(unitContentState.fulfilledTimeStamp).toISOString(),
          endTime: new Date().toISOString()
        };

        console.group("📊 Unit Activity Tracking - Lesson (Navigation)");
        console.log("🎯 Activity Data:", {
          featureId: lessonId,
          unitId: activityData.unitId,
          startTime: activityData.startTime,
          endTime: activityData.endTime,
          duration: new Date(activityData.endTime).getTime() - new Date(activityData.startTime).getTime(),
          durationMinutes: ((new Date(activityData.endTime).getTime() - new Date(activityData.startTime).getTime()) / 1000 / 60).toFixed(2)
        });

        // Validation checks
        if (activityData.startTime >= activityData.endTime) {
          console.error("❌ INVALID ACTIVITY TIME: Start time >= End time");
        }
        const activityDurationHours = (new Date(activityData.endTime).getTime() - new Date(activityData.startTime).getTime()) / 1000 / 60 / 60;
        if (activityDurationHours > 8) {
          console.error("❌ INVALID ACTIVITY TIME: Duration > 8 hours:", activityDurationHours.toFixed(2));
        }

        console.log("📤 Sending activity request to:", `/activities/${lessonId}`);

        postActivity({
          featureId: lessonId,
          createUnitActivityRequest: activityData
        }).then((response) => {
          console.log("📥 Activity Response:", response);
          console.log("✅ Activity tracking successful");
          activityState.reset();
          console.groupEnd();
        }).catch((error) => {
          console.error("🚨 Activity tracking error:", error);
          console.groupEnd();
        })
      }

      fetchUnitContent({ lessonId, unitId: currentUnitId });
      localStorage.setItem(`lesson:${lessonId}:currentUnitId`, currentUnitId.toString());
      unitCompletionState.reset();
    }
  }, [currentUnitId]);

  const handlePostUnitCompletion = (completion: IUnitCompleted) => {
    // ✅ Enhanced Learning Minutes: Mark session as completed to avoid double counting
    sessionTracker.markSessionCompleted();

    // Add unit completion debugging
    console.group("🎯 Unit Completion Debug - Lesson");
    console.log("📊 Unit Completion Data:", {
      unitId: completion.id,
      type: completion.type,
      startTime: completion.start,
      endTime: completion.end,
      duration: completion.start && completion.end ? new Date(completion.end).getTime() - new Date(completion.start).getTime() : null,
      durationMinutes: completion.start && completion.end ? ((new Date(completion.end).getTime() - new Date(completion.start).getTime()) / 1000 / 60).toFixed(2) : null
    });

    // Validation checks (same as backend)
    if (completion.start && completion.end && completion.start >= completion.end) {
      console.error("❌ INVALID TIME: Start time >= End time");
    }
    if (completion.start === null || completion.end === null) {
      console.error("❌ INVALID TIME: Start or End is null");
    }
    if (completion.start && completion.end) {
      const durationHours = (new Date(completion.end).getTime() - new Date(completion.start).getTime()) / 1000 / 60 / 60;
      if (durationHours > 8) {
        console.error("❌ INVALID TIME: Duration > 8 hours:", durationHours.toFixed(2));
      }
    }

    console.log("📤 Sending request to:", `/lessons/${lessonId}/units/${completion.id}`);
    console.log("🔄 Enhanced learning minutes: Session marked as completed - no duplicate watch activity");

    postUnitCompletion({
      lessonId,
      id: completion.id || 0,
      iUnitCompleted: completion,
    })
      .unwrap()
      .then((response) => {
        console.log("📥 Response received:", response);
        console.log("✅ SUCCESS: Unit completion accepted");
        console.log("📋 Response Data:", response);
        
        lessonState.refetch();
        unitsState.refetch();
        console.groupEnd();
      })
      .catch((error) => {
        console.error("🚨 Error while posting unit completion:", error);
        if (error.status === 400) {
          console.error("❌ BAD REQUEST (400): Backend validation failed");
          console.log("🔍 Check time data validation above");
        }
        console.groupEnd();
      });
  };

  return (
    <LessonContext.Provider
      value={{
        isLoading: lessonState.isLoading || unitsState.isLoading,
        isError: lessonState.isError || unitsState.isError,
        lesson: lessonState.data,
        units: unitsState.data || [],
        currentUnitId,
        setCurrentUnitId,
        unitContent: {
          isError: unitContentState.isError,
          isLoading:
            unitContentState.isUninitialized || unitContentState.isFetching,
          data:
            unitContentState.isFetching === false
              ? {
                  ...unitContentState.currentData,
                  isChat: (unitContentState.currentData?.unit as any)?.isChat,
                  isRevealSolution: lessonState.data?.isRevealSolution || false,
                }
              : undefined,
        },
        unitCompletion: {
          isLoading: unitCompletionState.isLoading,
          response: unitCompletionState.data,
          post: handlePostUnitCompletion,
        },
      }}
    >
      {children}
    </LessonContext.Provider>
  );
};
